import { zodResolver } from "@hookform/resolvers/zod";
import {
	Button,
	Group,
	Modal,
	MultiSelect,
	NumberInput,
	SegmentedControl,
	Select,
	Stack,
	Switch,
	Text,
	TextInput,
} from "@mantine/core";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
	emptyHotelPromoValues,
	type HotelPromoFormValues,
	hotelPromoSchema,
} from "@/features/hotel-promos/hotel-promo-schema";
import type { HotelOption, HotelPromo } from "@/features/hotel-promos/types";
import { GuestUserPicker } from "@/features/users/ui/guest-user-picker";

export type HotelPromoEditorState =
	| { mode: "create" }
	| { mode: "edit"; row: HotelPromo };

type Option = { value: string; label: string };

export function defaultsFor(
	editor: HotelPromoEditorState,
): HotelPromoFormValues {
	if (editor.mode === "edit") {
		const row = editor.row;
		return {
			code: row.code,
			phone: "",
			percent: row.percent,
			isActive: row.isActive,
			partnerId: row.targetType === "PARTNER" ? row.targetId : "",
			categoryId: row.targetType === "CATEGORY" ? row.targetId : "",
			hotelKeys: row.hotelKeys,
			targetType: row.targetType,
		};
	}
	return emptyHotelPromoValues();
}

export function HotelPromoEditorModal({
	editor,
	saving,
	hotels,
	partners,
	categories,
	hotelsError,
	onClose,
	onSave,
}: {
	editor: HotelPromoEditorState | null;
	saving: boolean;
	hotels: HotelOption[];
	partners: Option[];
	categories: Option[];
	hotelsError?: string;
	onClose: () => void;
	onSave: (values: HotelPromoFormValues) => void;
}) {
	const { t } = useTranslation();
	return (
		<Modal
			opened={editor !== null}
			onClose={onClose}
			title={
				editor?.mode === "edit" ? t("hotel.editTitle") : t("hotel.addTitle")
			}
			centered
			size="lg"
		>
			{editor ? (
				<HotelPromoForm
					key={`${editor.mode}-${editor.mode === "edit" ? editor.row.id : "new"}`}
					editor={editor}
					saving={saving}
					hotels={hotels}
					partners={partners}
					categories={categories}
					hotelsError={hotelsError}
					onClose={onClose}
					onSave={onSave}
				/>
			) : null}
		</Modal>
	);
}

function HotelPromoForm({
	editor,
	saving,
	hotels,
	partners,
	categories,
	hotelsError,
	onClose,
	onSave,
}: {
	editor: HotelPromoEditorState;
	saving: boolean;
	hotels: HotelOption[];
	partners: Option[];
	categories: Option[];
	hotelsError?: string;
	onClose: () => void;
	onSave: (values: HotelPromoFormValues) => void;
}) {
	const { t } = useTranslation();
	const editing = editor.mode === "edit";
	const form = useForm<HotelPromoFormValues>({
		resolver: zodResolver(hotelPromoSchema(editor.mode)),
		defaultValues: defaultsFor(editor),
	});
	const targetType = form.watch("targetType");
	const hotelOptions = hotels.map((hotel) => ({
		value: hotel.key,
		label: hotel.title,
	}));

	return (
		<form onSubmit={form.handleSubmit(onSave)} noValidate>
			<Stack>
				<TextInput
					label={t("hotel.servioCode")}
					description={t("hotel.codeHint")}
					error={form.formState.errors.code?.message}
					{...form.register("code")}
				/>
				<Controller
					name="percent"
					control={form.control}
					render={({ field }) => (
						<NumberInput
							label={t("hotel.percent")}
							description={t("hotel.percentHint")}
							min={1}
							max={100}
							allowDecimal={false}
							value={field.value}
							error={form.formState.errors.percent?.message}
							onChange={(value) => {
								field.onChange(typeof value === "number" ? value : undefined);
							}}
						/>
					)}
				/>
				<Controller
					name="hotelKeys"
					control={form.control}
					render={({ field }) => (
						<MultiSelect
							label={t("hotel.hotels")}
							description={hotelsError ?? t("hotel.hotelsHint")}
							data={hotelOptions}
							value={field.value}
							searchable
							error={form.formState.errors.hotelKeys?.message}
							onChange={field.onChange}
						/>
					)}
				/>
				{hotelsError ? (
					<Text c="red" size="sm">
						{hotelsError}
					</Text>
				) : null}
				<Controller
					name="isActive"
					control={form.control}
					render={({ field }) => (
						<Switch
							label={t("hotel.active")}
							description={t("hotel.activeHint")}
							checked={field.value}
							onChange={(event) => field.onChange(event.currentTarget.checked)}
						/>
					)}
				/>
				<div>
					<Text size="sm" fw={500} mb={6}>
						{t("hotel.targetType")}
					</Text>
					<Controller
						name="targetType"
						control={form.control}
						render={({ field }) => (
							<SegmentedControl
								fullWidth
								disabled={editing}
								value={field.value}
								data={[
									{ value: "CATEGORY", label: t("hotel.category") },
									{ value: "USER", label: t("hotel.user") },
									{ value: "PARTNER", label: t("hotel.partner") },
								]}
								onChange={(value) =>
									field.onChange(value as HotelPromoFormValues["targetType"])
								}
							/>
						)}
					/>
				</div>
				{editor.mode === "edit" ? (
					<TextInput
						label={t("hotel.boundTo")}
						value={editor.row.targetLabel}
						disabled
					/>
				) : null}
				{!editing && targetType === "CATEGORY" ? (
					<Controller
						name="categoryId"
						control={form.control}
						render={({ field }) => (
							<Select
								label={t("hotel.discountCategory")}
								description={t("hotel.categoryHint")}
								data={categories}
								value={field.value || null}
								searchable
								error={form.formState.errors.categoryId?.message}
								onChange={(value) => field.onChange(value ?? "")}
							/>
						)}
					/>
				) : null}
				{!editing && targetType === "USER" ? (
					<GuestUserPicker
						value={form.watch("phone") ? "picked" : ""}
						label={t("hotel.userField")}
						description={t("hotel.userHint")}
						error={form.formState.errors.phone?.message}
						onChange={(userId) => {
							if (!userId) {
								form.setValue("phone", "");
							}
						}}
						onPick={(guest) => {
							form.setValue("phone", guest.phone, { shouldValidate: true });
						}}
					/>
				) : null}
				{!editing && targetType === "PARTNER" ? (
					<Controller
						name="partnerId"
						control={form.control}
						render={({ field }) => (
							<Select
								label={t("hotel.partner")}
								description={t("hotel.partnerHint")}
								data={partners}
								value={field.value || null}
								searchable
								error={form.formState.errors.partnerId?.message}
								onChange={(value) => field.onChange(value ?? "")}
							/>
						)}
					/>
				) : null}
				<Text size="sm" c="dimmed">
					{t("hotel.ownerLocked")}
				</Text>
				<Group justify="flex-end">
					<Button variant="default" onClick={onClose} disabled={saving}>
						{t("common.cancel")}
					</Button>
					<Button type="submit" loading={saving}>
						{t("common.save")}
					</Button>
				</Group>
			</Stack>
		</form>
	);
}
